import React, { useCallback, useEffect, useState } from "react";
import {
    CheckCircle2, ChevronDown, Gauge, History, Info, Loader2, RotateCcw, ShieldCheck, Undo2, Wand2, XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { SeverityBadge } from "@/components/common/Badges";
import { ReasonDialog } from "@/components/common/ReasonDialog";
import { TID } from "@/lib/testIds";

// Confirmations that land on the audit trail stay on screen long enough to read.
const AUDIT_TOAST = { duration: 9000 };

const ACTION_LABEL = {
    restrict_field_value: "Restrict field",
    set_org_level: "Set org level",
    remove_auth_object: "Remove object",
    add_auth_objects: "Add objects",
    remove_tcode: "Remove transactions",
    replace_tcode: "Replace transaction",
    migrate_to_catalog: "Migrate to Fiori catalogs",
    advisory: "Advisory only",
};

function ScoreGauge({ score, open }) {
    const tone = score >= 85 ? "text-emerald-300" : score >= 60 ? "text-amber-300" : "text-rose-300";
    return (
        <div className="flex items-center gap-3" data-testid={TID.bpScore}>
            <Gauge size={18} className={tone} />
            <div>
                <div className={`text-2xl font-mono ${tone}`}>{score}<span className="text-slate-500 text-sm">/100</span></div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-slate-400">
                    conformance · {open} open proposal{open === 1 ? "" : "s"}
                </div>
            </div>
        </div>
    );
}

function ProposalCard({ p, selected, onToggle, values, onValues, onDismiss, busy }) {
    const [expanded, setExpanded] = useState(false);
    const advisory = p.change.action === "advisory";
    return (
        <div
            data-testid={TID.bpProposal(p.fingerprint)}
            className="border border-white/10 rounded-[4px] p-3 space-y-2 hover:border-white/20 transition-colors"
        >
            <div className="flex items-start gap-2">
                {!advisory && (
                    <input
                        type="checkbox"
                        data-testid={TID.bpProposalSelect(p.fingerprint)}
                        checked={selected}
                        onChange={onToggle}
                        className="mt-1 accent-cyan-400"
                        aria-label={`Select ${p.rule_id}`}
                    />
                )}
                <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-1.5">
                        <SeverityBadge severity={p.severity} />
                        <span className="font-mono text-[10px] text-cyan-300">{p.rule_id}</span>
                        <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm border bg-white/5 text-slate-300 border-white/10">
                            {ACTION_LABEL[p.change.action] || p.change.action}
                        </span>
                        <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm border bg-violet-500/10 text-violet-300 border-violet-500/25">
                            {p.provenance}
                        </span>
                        {p.requires_input && (
                            <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm border bg-amber-500/10 text-amber-300 border-amber-500/25">
                                needs values
                            </span>
                        )}
                    </div>
                    <p className="text-sm text-slate-200 mt-1">{p.finding}</p>
                    <p className="text-[11px] text-cyan-200/80 mt-1">{p.change_summary}</p>
                </div>
            </div>

            {p.requires_input && (
                <input
                    data-testid={TID.bpProposalValues(p.fingerprint)}
                    value={values}
                    onChange={(e) => onValues(e.target.value)}
                    placeholder={p.suggested_values?.length ? `e.g. ${p.suggested_values.join(", ")}` : "Values, comma separated (e.g. 1000, 2000)"}
                    className="field text-xs"
                />
            )}

            <div className="flex flex-wrap items-center gap-2">
                <button
                    data-testid={TID.bpProposalExpand(p.fingerprint)}
                    onClick={() => setExpanded((v) => !v)}
                    className="btn-ghost inline-flex items-center gap-1 text-[11px]"
                >
                    <ChevronDown size={12} className={`transition-transform ${expanded ? "rotate-180" : ""}`} />
                    Why this matters
                </button>
                <button
                    data-testid={TID.bpProposalDismiss(p.fingerprint)}
                    onClick={onDismiss}
                    disabled={busy}
                    className="btn-ghost inline-flex items-center gap-1 text-[11px] disabled:opacity-50"
                >
                    <XCircle size={12} /> Accept risk
                </button>
            </div>

            {expanded && (
                <div className="text-[11px] text-slate-400 space-y-1.5 border-t border-white/5 pt-2">
                    <p>{p.rationale}</p>
                    {Object.keys(p.evidence || {}).length > 0 && (
                        <pre className="font-mono text-[10px] text-slate-300 bg-black/40 border border-white/5 rounded-sm p-2 overflow-x-auto">
                            {JSON.stringify(p.evidence, null, 1)}
                        </pre>
                    )}
                    {(p.references || []).map((r) => (
                        <p key={r} className="text-[10px] text-slate-400">· {r}</p>
                    ))}
                </div>
            )}
        </div>
    );
}

/** Dynamic PFCG Role Content Update — diffs one role against the active
 * best-practice pack and applies the approved changes to UTSAVA's role model. */
export function BestPracticeTab({ role, onUpdated }) {
    const [scan, setScan] = useState(null);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [selected, setSelected] = useState({});
    const [values, setValues] = useState({});
    const [impact, setImpact] = useState(null);
    const [showHistory, setShowHistory] = useState(false);
    const [historyKey, setHistoryKey] = useState(0);
    const [pendingDismiss, setPendingDismiss] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/roles/${role.id}/best-practice/scan`);
            setScan(data);
            setSelected({});
        } catch {
            toast.error("Best-practice scan failed");
        } finally {
            setLoading(false);
        }
    }, [role.id]);

    useEffect(() => { load(); }, [load]);

    const apply = async () => {
        const items = Object.keys(selected).filter((f) => selected[f]).map((f) => ({
            fingerprint: f,
            values: (values[f] || "").split(",").map((v) => v.trim()).filter(Boolean),
        }));
        if (!items.length) {
            toast.warning("Select at least one proposal");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post(`/roles/${role.id}/best-practice/apply`, { items });
            setImpact(data.impact);
            if (data.applied.length) {
                toast.success(`${data.applied.length} change(s) applied · ${data.impact.proposals_resolved} proposal(s) resolved`, AUDIT_TOAST);
                onUpdated?.(data.role);
            }
            data.skipped.forEach((s) => toast.warning(`${s.rule_id}: ${s.reason}`));
            setHistoryKey((k) => k + 1);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Apply failed");
            await load();
        } finally {
            setBusy(false);
        }
    };

    const dismiss = (fingerprint) => setPendingDismiss(fingerprint);

    const submitDismiss = async (reason) => {
        const fingerprint = pendingDismiss;
        setPendingDismiss(null);
        setBusy(true);
        try {
            const { data } = await api.post(`/roles/${role.id}/best-practice/dismiss`, { fingerprint, reason });
            setScan(data);
            setHistoryKey((k) => k + 1);
            toast.success("Recorded as accepted risk");
        } catch {
            toast.error("Could not record the acceptance");
        } finally {
            setBusy(false);
        }
    };

    const restore = async (fingerprint) => {
        setBusy(true);
        try {
            const { data } = await api.post(`/roles/${role.id}/best-practice/restore/${fingerprint}`);
            setScan(data);
            setHistoryKey((k) => k + 1);
            toast.success("Proposal reopened");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not reopen the proposal");
        } finally {
            setBusy(false);
        }
    };

    const rollback = async (fingerprint) => {
        setBusy(true);
        try {
            const { data } = await api.post(`/roles/${role.id}/best-practice/rollback/${fingerprint}`);
            setScan(data);
            setImpact(null);
            onUpdated?.(data.role);
            toast.success(`Rolled back ${data.rolled_back.rule_id} — ${data.rolled_back.change_summary}`, AUDIT_TOAST);
            setHistoryKey((k) => k + 1);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not roll back the change");
        } finally {
            setBusy(false);
        }
    };

    if (loading && !scan) {
        return <div className="flex items-center gap-2 text-sm text-slate-400 py-6"><Loader2 size={14} className="animate-spin" /> Diffing against the best-practice pack…</div>;
    }
    if (!scan) return <p className="text-sm text-slate-400 py-6">Best-practice scan unavailable.</p>;

    const selectedCount = Object.values(selected).filter(Boolean).length;

    return (
        <div className="space-y-3" data-testid={TID.bpTabPanel}>
            <div className="flex flex-wrap items-center justify-between gap-3 border border-white/10 rounded-[4px] p-3">
                <ScoreGauge score={scan.score} open={scan.open_count} />
                <div className="flex flex-wrap items-center gap-1.5 text-[10px] font-mono">
                    {Object.entries(scan.by_severity).map(([sev, n]) => (
                        <span key={sev} className="px-1.5 py-0.5 rounded-sm bg-white/5 text-slate-300 border border-white/10">
                            {sev} · {n}
                        </span>
                    ))}
                    <span className="px-1.5 py-0.5 rounded-sm bg-white/5 text-slate-400 border border-white/10">
                        pack {scan.pack_version}
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        data-testid={TID.bpApplySelected}
                        onClick={apply}
                        disabled={busy || !selectedCount}
                        className="btn-primary inline-flex items-center gap-1.5 text-xs disabled:opacity-50"
                    >
                        {busy ? <Loader2 size={12} className="animate-spin" /> : <Wand2 size={12} />}
                        Apply {selectedCount || ""} selected
                    </button>
                    <button
                        data-testid={TID.bpRescan}
                        onClick={load}
                        disabled={loading}
                        className="btn-ghost inline-flex items-center gap-1.5 text-xs disabled:opacity-50"
                    >
                        <RotateCcw size={12} /> Re-scan
                    </button>
                </div>
            </div>

            <p className="text-[10px] font-mono text-slate-400 flex items-start gap-1.5">
                <Info size={11} className="mt-0.5 shrink-0" />
                Changes are applied to the UTSAVA role model only — SoD and critical access are re-evaluated
                immediately, and the result reaches PFCG through the .SAP export.
            </p>

            {impact && (
                <div data-testid={TID.bpImpact} className="border border-cyan-500/25 rounded-[4px] p-3 text-[11px] font-mono text-slate-300 flex flex-wrap gap-3">
                    <span className="text-cyan-300">impact</span>
                    <span>proposals {impact.before.open_proposals} → {impact.after.open_proposals}</span>
                    <span>score {impact.before.score} → {impact.after.score}</span>
                    <span>SoD conflicts {impact.before.conflicts} → {impact.after.conflicts}</span>
                    <span>critical access {impact.before.critical} → {impact.after.critical}</span>
                    <span>risk {impact.before.risk_level} → {impact.after.risk_level}</span>
                </div>
            )}

            {scan.proposals.length === 0 ? (
                <div data-testid={TID.bpClean} className="flex items-center gap-2 text-sm text-emerald-300 border border-emerald-500/25 rounded-[4px] p-3">
                    <ShieldCheck size={14} /> This role conforms to every active best-practice rule.
                </div>
            ) : (
                <div className="space-y-2">
                    {scan.proposals.map((p) => (
                        <ProposalCard
                            key={p.fingerprint}
                            p={p}
                            busy={busy}
                            selected={!!selected[p.fingerprint]}
                            onToggle={() => setSelected((s) => ({ ...s, [p.fingerprint]: !s[p.fingerprint] }))}
                            values={values[p.fingerprint] || ""}
                            onValues={(v) => setValues((s) => ({ ...s, [p.fingerprint]: v }))}
                            onDismiss={() => dismiss(p.fingerprint)}
                        />
                    ))}
                </div>
            )}

            <button
                data-testid={TID.bpHistoryToggle}
                onClick={() => setShowHistory((v) => !v)}
                className="btn-ghost inline-flex items-center gap-1.5 text-[11px]"
            >
                <History size={12} /> {showHistory ? "Hide" : "Show"} decision history
                {scan.dismissed_count ? ` (${scan.dismissed_count} accepted)` : ""}
            </button>

            {showHistory && (
                <HistoryPanel
                    roleId={role.id}
                    refreshKey={historyKey}
                    onRestore={restore}
                    onRollback={rollback}
                    busy={busy}
                />
            )}

            <ReasonDialog
                open={!!pendingDismiss}
                testId={TID.bpDismissDialog}
                title="Accept this risk"
                description="The proposal stops resurfacing for this role. The reason and your name are written to the audit trail."
                label="Justification"
                placeholder="e.g. Business-critical for month-end close; compensating control MC-009 is tested quarterly."
                confirmLabel="Accept risk"
                required
                onConfirm={submitDismiss}
                onCancel={() => setPendingDismiss(null)}
            />
        </div>
    );
}

function HistoryPanel({ roleId, refreshKey, onRestore, onRollback, busy }) {
    const [data, setData] = useState(null);
    useEffect(() => {
        api.get(`/roles/${roleId}/best-practice/history`).then(({ data }) => setData(data)).catch(() => setData(null));
    }, [roleId, refreshKey]);
    if (!data) return <p className="text-[11px] text-slate-400">Loading history…</p>;
    return (
        <div data-testid={TID.bpHistory} className="border border-white/10 rounded-[4px] p-3 space-y-2 text-[11px]">
            <p className="font-mono uppercase tracking-wider text-slate-400 text-[10px]">Applied</p>
            {data.applied.length === 0 && <p className="text-slate-400">Nothing applied yet.</p>}
            {data.applied.map((a) => (
                <div key={a.fingerprint + a.applied_at} className="flex flex-wrap items-center gap-2 text-slate-300">
                    <CheckCircle2 size={12} className="text-emerald-400" />
                    <span className="font-mono text-[10px] text-cyan-300">{a.rule_id}</span>
                    <span>{a.change_summary}</span>
                    <span className="text-slate-400 font-mono text-[10px]">{a.applied_by} · {a.applied_at?.slice(0, 16).replace("T", " ")}</span>
                    {a.can_rollback && (
                        <button
                            data-testid={TID.bpRollback(a.fingerprint)}
                            onClick={() => onRollback(a.fingerprint)}
                            disabled={busy}
                            className="btn-ghost inline-flex items-center gap-1 text-[10px] disabled:opacity-50"
                            title="Restore the role content exactly as it was before this change"
                        >
                            <Undo2 size={11} /> Roll back
                        </button>
                    )}
                </div>
            ))}
            {data.applied.length > 1 && (
                <p className="text-[10px] font-mono text-slate-500">
                    Only the newest change is undoable — roll back in reverse order.
                </p>
            )}
            <p className="font-mono uppercase tracking-wider text-slate-400 text-[10px] pt-2 border-t border-white/5">Accepted risks</p>
            {data.dismissed.length === 0 && <p className="text-slate-400">None.</p>}
            {data.dismissed.map((d) => (
                <div key={d.fingerprint} className="flex flex-wrap items-center gap-2 text-slate-300">
                    <XCircle size={12} className="text-amber-400" />
                    <span className="font-mono text-[10px] text-cyan-300">{d.rule_id}</span>
                    <span>{d.reason || "no reason recorded"}</span>
                    <span className="text-slate-400 font-mono text-[10px]">{d.decided_by}</span>
                    <button
                        data-testid={TID.bpRestore(d.fingerprint)}
                        onClick={() => onRestore(d.fingerprint)}
                        disabled={busy}
                        className="btn-ghost text-[10px] disabled:opacity-50"
                    >
                        Reopen
                    </button>
                </div>
            ))}
        </div>
    );
}
